/**
 * ExportModal Component
 * Modal for exporting data in Excel or CSV format
 */

import React, { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Select } from './Select';
import { Download, FileSpreadsheet, FileText, AlertCircle } from 'lucide-react';
import { exportService, ExportType, ExportFormat } from '../../services/export.service';
import { getErrorMessage } from '../../services/api';
import { toast } from 'react-hot-toast';

export interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: ExportType;
  availableTypes?: ExportType[];
  title?: string;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  defaultType = 'objectives',
  // 'users' (the full tenant roster) is deliberately NOT in this default — it is admin-only
  // on the server, so a call site that wants to offer it must ask for it explicitly.
  availableTypes = ['objectives', 'key_results', 'both'],
  title = 'Export Data',
}) => {
  const [exportType, setExportType] = useState<ExportType>(defaultType);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('excel');
  const [isExporting, setIsExporting] = useState(false);

  const exportTypeOptions = [
    { value: 'objectives', label: 'Objectives Only', disabled: !availableTypes.includes('objectives') },
    { value: 'key_results', label: 'Key Results Only', disabled: !availableTypes.includes('key_results') },
    { value: 'both', label: 'Objectives & Key Results', disabled: !availableTypes.includes('both') },
    { value: 'users', label: 'Users', disabled: !availableTypes.includes('users') },
  ];

  const formatOptions = [
    { value: 'excel', label: 'Excel (.xlsx)', disabled: false },
    { value: 'csv', label: 'CSV (.csv)', disabled: exportType === 'both' }, // CSV doesn't support multiple sheets
  ];

  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Validate format for 'both' type
      if (exportType === 'both' && exportFormat === 'csv') {
        toast.error('CSV format does not support exporting both objectives and key results. Please use Excel format.');
        return;
      }

      await exportService.exportData(exportType, exportFormat);

      toast.success(`Export completed successfully!`);
      onClose();
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(`Export failed: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as ExportType;
    setExportType(newType);

    // If 'both' is selected, force Excel format
    if (newType === 'both' && exportFormat === 'csv') {
      setExportFormat('excel');
    }
  };

  const getExportDescription = (): string => {
    switch (exportType) {
      case 'objectives':
        return 'Export all objectives with their details including title, description, owner, status, dates, and more.';
      case 'key_results':
        return 'Export all key results with their details including title, parent objective, owner, progress, target values, and more.';
      case 'both':
        return 'Export both objectives and key results in separate sheets within a single Excel file.';
      case 'users':
        return 'Export user data including name, email, role, department, and status (excluding sensitive information).';
      default:
        return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="space-y-4">
        {/* Export Type Selection */}
        <Select
          label="What to Export"
          value={exportType}
          onChange={handleTypeChange}
          options={exportTypeOptions}
          fullWidth
        />

        {/* Format Selection */}
        <Select
          label="Export Format"
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
          options={formatOptions}
          fullWidth
        />

        {/* Description */}
        <div className="rounded-md bg-blue-50 p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              {exportFormat === 'excel' ? (
                <FileSpreadsheet className="h-5 w-5 text-blue-400" />
              ) : (
                <FileText className="h-5 w-5 text-blue-400" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">{getExportDescription()}</p>
            </div>
          </div>
        </div>

        {/* Warning for CSV with 'both' type */}
        {exportType === 'both' && (
          <div className="rounded-md bg-yellow-50 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  CSV format does not support multiple data types in one file. Please use Excel format to export both objectives and key results together.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting || (exportType === 'both' && exportFormat === 'csv')}
            icon={Download}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
